import { ApolloProvider } from '@apollo/client';
import { SearchkitClient, SearchkitProvider } from '@searchkit/client';
import type { ReactElement } from 'react';
import React, { useEffect, useMemo } from 'react';
import { FILTER_IDENTIFIERS, LAZY_LOAD_PAGE_SIZE } from '../../../constants/constants';
import { ENTITY_CONSTANTS } from '../../../constants/entityConstants';
import { FEATURE_CONSTANTS } from '../../../constants/featureConstants';
import { useI18n } from '../../../i18n/I18n';
import ListView from '../../../list/ListView';
import { searchClient, slimResultsQuery } from '../../../list/slimQuery';
import { SortModel } from '../../filtering/filters';
import { useEngagementTracker } from './EngagementContext';
import EngagementTrackerDataGrid from './EngagementTrackerDataGrid';

interface EngagementTrackerDataGridWrapperProps {
    refetch?: () => void;
}

function EngagementTrackerDataGridWrapper({ refetch, ...rest }: EngagementTrackerDataGridWrapperProps): ReactElement {
    const { registerRefetch } = useEngagementTracker();

    useEffect(() => {
        if (refetch) {
            registerRefetch(async () => {
                refetch();
            });
        }
    }, [refetch, registerRefetch]);

    return <EngagementTrackerDataGrid {...rest} />;
}

export default function EngagementTracker({ personId }: { personId: string }): ReactElement {
    const searchkit = useMemo(() => new SearchkitClient(), []);
    const t = useI18n();

    const immutableFilters = useMemo(
        () => [
            { identifier: FILTER_IDENTIFIERS.entity, value: ENTITY_CONSTANTS.engagement },
            { identifier: FILTER_IDENTIFIERS.referencedId, value: personId }
        ],
        [personId]
    );

    return (
        <ApolloProvider client={searchClient}>
            <SearchkitProvider client={searchkit}>
                <ListView
                    key={personId}
                    containerSx={{
                        p: 0,
                        gap: 0,
                        '& .MuiDataGrid-root': {
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0
                        },
                        height: '100%'
                    }}
                    headerTitle={t('person.profile.engagement')}
                    id="engagement-tracker"
                    entity={ENTITY_CONSTANTS.engagement}
                    feature={FEATURE_CONSTANTS.engagementTracker}
                    query={slimResultsQuery(ENTITY_CONSTANTS.engagement)}
                    immutableFilters={immutableFilters}
                    showBreadCrumbs={false}
                    defaultSortModel={{ field: 'createdOn', sort: 'desc' } as SortModel}
                    EntityTableRenderer={EngagementTrackerDataGridWrapper}
                    showReportButton={false}
                    customPageSize={LAZY_LOAD_PAGE_SIZE}
                    saveFiltersDisabled
                />
            </SearchkitProvider>
        </ApolloProvider>
    );
}
